// @ts-check
// Configurações > Funil e etapas: edita as etapas de enrollmentOpportunity.stage, que o motor espelha nas etapas do
// aluno (stageOverride e effectiveStage). LEAD, ENROLLED, LOST e NOT_A_LEAD ficam protegidas e nas extremidades. Com
// papéis restritos por etapa (contrato de equipe, seção 3.2), a etapa nova nasce oculta para eles e a exclusão avisa
// quem deixa de ver os cards movidos; quem também gerencia papéis pode liberar a etapa nova ao salvar.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import {
  FINAL_FUNNEL_STAGES, FUNNEL_STAGE_FIELD_PATHS, UNDELETABLE_FUNNEL_STAGES, pinProtectedFunnelStages,
} from '../../../../../shared/metadata/funnel-stages.js';
import { navigate } from '../../../app/router.js';
import { fetchFieldOptionUsage, fetchFieldOptionUsageWithScopes, updateFieldMetadata } from '../../../data/data-model.js';
import { allObjectsState, hasCurrentPermissionFlag, viewsState } from '../../../data/metadata-store.js';
import { saveRole } from '../../../data/team.js';
import { Checkbox } from '../../../ui/feedback.js';
import {
  findRolesLosingMigratedCards, releaseStageInScope, suggestNewStageReleases,
} from '../../../../../shared/permissions/funnel-stage-scope.js';
import { Button } from '../../../ui/button.js';
import { Skeleton } from '../../../ui/feedback.js';
import { Icon } from '../../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../../i18n/index.js';
import { PageCard, PageHeader } from '../../page/page-card.js';
import { SettingsSection } from '../settings-pages.js';
import { canEditDataModel, showDataModelError } from './data-model-shared.js';
import { OptionRemovalModal } from './option-removal-modal.js';
import { StageRequirementsSettings } from './settings-stage-requirements.js';
import { SelectOptionsEditor, findRemovedOptions, toApiOptions, toDraftOptions, validateDraftOptions } from './select-options-editor.js';

/**
 * @typedef {import('./select-options-editor.js').DraftOption} DraftOption
 * @typedef {import('../../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../../data/data-model.js').FunnelStageScopeSummary} FunnelStageScopeSummary
 */

/** Campo de origem das etapas e o campo de etapa do aluno (para contar alunos por etapa). */
function useFunnelFields() {
  const [[cardObjectName, cardFieldName], , [studentObjectName, studentFieldName]] = FUNNEL_STAGE_FIELD_PATHS;
  const objects = allObjectsState.value;
  const cardObject = objects.find((object) => object.nameSingular === cardObjectName);
  const studentObject = objects.find((object) => object.nameSingular === studentObjectName);
  return {
    cardObject,
    stageField: cardObject?.fields.find((field) => field.name === cardFieldName),
    studentStageField: studentObject?.fields.find((field) => field.name === studentFieldName),
  };
}

/** @param {FieldMetadata} stageField */
const toFunnelDraft = (stageField) => pinProtectedFunnelStages(toDraftOptions(stageField.options));

export function SettingsFunnelPage() {
  const { cardObject, stageField, studentStageField } = useFunnelFields();
  const isEditable = canEditDataModel();
  const [options, setOptions] = useState(/** @type {DraftOption[]} */ (stageField ? toFunnelDraft(stageField) : []));
  const [cardUsage, setCardUsage] = useState(/** @type {Record<string, number> | null} */ (null));
  const [studentUsage, setStudentUsage] = useState(/** @type {Record<string, number>} */ ({}));
  const [removed, setRemoved] = useState(/** @type {Array<{ value: string, label: string, color?: string }> | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const [stageScopes, setStageScopes] = useState(/** @type {FunnelStageScopeSummary[]} */ ([]));
  // escolhas da pessoa para liberar etapas novas (papel:valor); sem escolha, vale a sugestão das vizinhas
  const [releaseChoices, setReleaseChoices] = useState(/** @type {Record<string, boolean>} */ ({}));
  const canManageRoles = hasCurrentPermissionFlag('ROLES');

  const loadUsage = () => {
    if (!stageField || !isEditable) return;
    Promise.all([fetchFieldOptionUsageWithScopes(stageField.id), studentStageField ? fetchFieldOptionUsage(studentStageField.id) : Promise.resolve({})])
      .then(([cards, students]) => {
        setCardUsage(cards.optionUsage);
        setStageScopes(cards.stageScopes);
        setStudentUsage(students);
      })
      .catch(() => setCardUsage({}));
  };
  useEffect(() => {
    document.title = `${t('dataModel.funnel.title')} - ${t('navigation.settings')}`;
    loadUsage();
  }, [stageField?.id]);

  if (!stageField || !cardObject) {
    return html`<${PageCard} header=${html`<${PageHeader} icon="IconLayoutKanban" title=${t('dataModel.funnel.title')} />`}>
      <div class="settings-page"><p class="data-model-empty-hint">${t('dataModel.funnel.unavailable')}</p></div>
    <//>`;
  }

  const original = toFunnelDraft(stageField);
  const isDirty = JSON.stringify(toApiOptions(options)) !== JSON.stringify(toApiOptions(original));
  const error = validateDraftOptions(options);
  const board = viewsState.value.find((view) => view.type === 'KANBAN' && view.mainGroupByFieldMetadataId === stageField.id);
  const combinedUsage = Object.fromEntries((stageField.options ?? []).map((option) => [option.value, (cardUsage?.[option.value] ?? 0) + (studentUsage[option.value] ?? 0)]));
  /** @param {number} _count @param {string} value */
  const usageLabel = (_count, value) => {
    const cards = cardUsage?.[value] ?? 0;
    const students = studentUsage[value] ?? 0;
    if (cards === 0 && students === 0) return t('dataModel.funnel.emptyStage');
    return `${t('dataModel.funnel.cardCount', { count: cards })} · ${t('dataModel.funnel.studentCount', { count: students })}`;
  };

  // etapas novas (sem id no rascunho) nascem ocultas para os papéis restritos
  const originalIds = new Set((stageField.options ?? []).map((option) => option.id));
  const newStages = options.filter((option) => !option.id || !originalIds.has(option.id)).filter((option) => option.value);
  const orderedValues = options.map((option) => option.value);
  const releaseKey = (/** @type {string} */ roleId, /** @type {string} */ value) => `${roleId}:${value}`;
  /** @param {string} roleId @param {string} value */
  const isReleaseChecked = (roleId, value) => releaseChoices[releaseKey(roleId, value)]
    ?? suggestNewStageReleases(orderedValues, value, stageScopes).includes(roleId);
  const labelOf = (/** @type {string} */ value) => {
    const option = options.find((candidate) => candidate.value === value) ?? stageField.options?.find((candidate) => candidate.value === value);
    return translateMetadataLabel(option?.label ?? value);
  };
  /** @param {string} fromValue @param {string} toValue */
  const migrationWarnings = (fromValue, toValue) => findRolesLosingMigratedCards(stageScopes, fromValue, toValue, cardUsage ? cardUsage[fromValue] ?? 0 : null)
    .map((scope) => t('dataModel.funnel.migrationHiddenForRoles', { from: labelOf(fromValue), to: labelOf(toValue), role: scope.roleLabel }));

  /**
   * Libera em Ver as etapas novas marcadas, relendo os papéis depois de salvar (renomeações já migradas pelo motor).
   * @param {string[]} values
   */
  const releaseNewStages = async (values) => {
    if (!canManageRoles || values.length === 0 || stageScopes.length === 0) return;
    const chosen = stageScopes.map((scope) => ({ scope, values: values.filter((value) => isReleaseChecked(scope.roleId, value)) }))
      .filter((item) => item.values.length > 0);
    if (chosen.length === 0) return;
    const fresh = await fetchFieldOptionUsageWithScopes(stageField.id).then((result) => result.stageScopes).catch(() => stageScopes);
    for (const { scope, values: releasedValues } of chosen) {
      const current = fresh.find((item) => item.roleId === scope.roleId) ?? scope;
      const next = releasedValues.reduce((accumulator, value) => releaseStageInScope(accumulator, value), {
        visibleStageValues: current.visibleStageValues, handoffStageValues: current.handoffStageValues,
      });
      try {
        await saveRole(scope.roleId, { funnelStageScope: next });
      } catch {
        showErrorSnackbar(t('dataModel.funnel.releaseFailed', { role: scope.roleLabel }));
      }
    }
  };

  /** @param {Record<string, string | null> | undefined} optionMigrations */
  const persist = async (optionMigrations) => {
    setIsSaving(true);
    try {
      const createdValues = newStages.map((option) => option.value);
      const updated = await updateFieldMetadata(stageField.id, { options: toApiOptions(options), ...(optionMigrations ? { optionMigrations } : {}) });
      // etapas novas ganham id no motor: o rascunho passa a ser a lista salva
      if (updated?.options) setOptions(toFunnelDraft(updated));
      await releaseNewStages(createdValues);
      showSuccessSnackbar(t('dataModel.funnel.saved'));
      setRemoved(null);
      setReleaseChoices({});
      loadUsage();
    } catch (saveError) {
      showDataModelError(saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const save = () => {
    if (!isDirty || error) return;
    const removedOptions = findRemovedOptions(stageField.options ?? [], options);
    // etapa excluída sempre pede destino: card e alunos não podem ficar sem etapa
    if (removedOptions.length > 0) setRemoved(removedOptions);
    else persist(undefined);
  };

  const actions = html`<div class="data-model-header-actions">
    ${board
      ? html`<${Button} title=${t('dataModel.funnel.seeBoard')} icon="IconLayoutKanban" variant="secondary" size="small"
          onClick=${() => navigate(`/objects/${cardObject.namePlural}?viewId=${board.id}`)} />`
      : null}
    ${isEditable && isDirty ? html`<${Button} title=${t('dataModel.funnel.discard')} variant="secondary" size="small" onClick=${() => setOptions(original)} />` : null}
    ${isEditable ? html`<${Button} title=${t('common.save')} icon="IconCheck" accent="blue" size="small" disabled=${!isDirty || Boolean(error)} isLoading=${isSaving} onClick=${save} />` : null}
  </div>`;

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconLayoutKanban" title=${t('dataModel.funnel.title')} actions=${actions} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('dataModel.funnel.stagesTitle')} description=${t('dataModel.funnel.description')}>
        <ul class="data-model-funnel-rules">
          <li><${Icon} name="IconLock" size=${14} /><span>${t('dataModel.funnel.ruleProtected')}</span></li>
          <li><${Icon} name="IconArrowsSort" size=${14} /><span>${t('dataModel.funnel.ruleOrder')}</span></li>
          <li><${Icon} name="IconArrowRight" size=${14} /><span>${t('dataModel.funnel.ruleRemoval')}</span></li>
          <li><${Icon} name="IconUserOff" size=${14} /><span>${t('dataModel.funnel.ruleNotLead')}</span></li>
        </ul>
        ${isEditable && cardUsage === null
          ? html`<${Skeleton} height=${320} radius=${8} />`
          : html`<${SelectOptionsEditor} options=${options} onChange=${setOptions} usage=${isEditable ? combinedUsage : null} usageLabel=${usageLabel}
              protectedValues=${UNDELETABLE_FUNNEL_STAGES} arrangeOptions=${pinProtectedFunnelStages} disabled=${!isEditable}
              addLabel=${t('dataModel.funnel.addStage')} />`}
        ${error && isDirty ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
        ${isEditable && isDirty && newStages.length > 0 && stageScopes.length > 0
          ? html`<div class="data-model-stage-release" data-testid="funnel-new-stage-roles" role="note">
              <span class="data-model-stage-release-title"><${Icon} name="IconEyeOff" size=${14} />${t('dataModel.funnel.newStagesTitle')}</span>
              <span>${t('dataModel.funnel.newStageHiddenForRoles', { count: stageScopes.length, roles: stageScopes.map((scope) => scope.roleLabel).join(', ') })}</span>
              ${canManageRoles
                ? html`${newStages.flatMap((option) => stageScopes.map((scope) => html`<label key=${releaseKey(scope.roleId, option.value)} class="data-model-stage-release-row">
                    <${Checkbox} checked=${isReleaseChecked(scope.roleId, option.value)}
                      ariaLabel=${t('dataModel.funnel.releaseNewStage', { stage: option.label || option.value, role: scope.roleLabel })}
                      onChange=${(/** @type {boolean} */ checked) => setReleaseChoices({ ...releaseChoices, [releaseKey(scope.roleId, option.value)]: checked })} />
                    <span>${t('dataModel.funnel.releaseNewStage', { stage: option.label || option.value, role: scope.roleLabel })}</span>
                  </label>`))}
                  <span class="input-hint">${t('dataModel.funnel.releaseHint')}</span>`
                : null}
            </div>`
          : null}
        ${!isEditable ? html`<p class="input-hint"><${Icon} name="IconLock" size=${12} /> ${t('dataModel.common.adminOnly')}</p>` : null}
      <//>
      <${StageRequirementsSettings} stageField=${stageField} />
      <${SettingsSection} title=${t('dataModel.funnel.whereTitle')} description=${t('dataModel.funnel.whereDescription')}>
        <ul class="data-model-funnel-rules">
          <li><${Icon} name="IconLayoutKanban" size=${14} /><span>${t('dataModel.funnel.whereBoard')}</span></li>
          <li><${Icon} name="IconUsers" size=${14} /><span>${t('dataModel.funnel.whereStudents')}</span></li>
          <li><${Icon} name="IconChartBar" size=${14} /><span>${t('dataModel.funnel.whereDashboards')}</span></li>
        </ul>
      <//>
    </div>
    ${removed
      ? html`<${OptionRemovalModal} removedOptions=${removed} targetOptions=${toApiOptions(options)} usage=${combinedUsage} usageLabel=${usageLabel}
          allowEmpty=${false} excludedTargets=${FINAL_FUNNEL_STAGES} isSaving=${isSaving} targetWarnings=${stageScopes.length > 0 ? migrationWarnings : undefined}
          onCancel=${() => setRemoved(null)} onConfirm=${(migrations) => persist(migrations)} />`
      : null}
  <//>`;
}
